import React, { useState, useEffect, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useShallow } from "zustand/react/shallow"
import { debounce } from "lodash"
import { useLocation } from "react-router-dom"

// components
import Loading from "components/Loading/Loading"
import RJForm from "./RJS/RJForm"
import SitesGrid from "./subcomponents/SitesGrid/SitesGrid"
import PagePreview from "./subcomponents/PagePreview/PagePreview"
import { Tooltip, Flex, FloatButton } from "antd"
import ForkDeleteModal from "./RJS/components/ForkDeleteModal"

import Modal from "components/Modal/Modal"
import { PrimeButton } from "components-design-system"

// templates
import ObjectFieldTemplate from "./RJS/templates/ObjectFieldTemplate"
import ArrayFieldTemplate from "./RJS/templates/ArrayFieldTemplate"

// utils
import { getVersionValues } from "./utils"
import queryKeys from "api/utils/queryKeys"
import { formSchemas } from "constants/authoring"
import { getHiddenNodes } from "./utils"

// api data
import PrimeField from "components/PrimeField/PrimeField"

// context
import useAuthoringViewStore from "./hooks/stores/useAuthoringViewStore"
import useAppState from "context/hooks/useAppState"

// apis
import { useUserPermissions } from "api/hooks"
import { useGetSiteManagementLocations } from "api/hooks/siteManagement/useSiteManagementApi"
import { useGetAuthoringVersion } from "api/hooks/studies/useAuthoringApi"
import { useGetLockedFields } from "api/hooks/admin/lockedFieldsApis"

// hooks
import { useAutosave } from "react-autosave"
import useAuthoringComments from "./hooks/actions/useAuthoringCommentActions"
import useAuthoringValidationsStore from "./hooks/stores/useAuthoringValidationsStore"
import useAuthoringDataStore from "./hooks/stores/useAuthoringDataStore"
import useAuthoringFieldStore from "./hooks/stores/useAuthoringFieldStore"
import useAuthoringVersionStore from "./hooks/stores/useAuthoringVersionStore"
import useAuthoringStore from "./hooks/stores/useAuthoringStore"
import useAuthoringActions from "./hooks/actions/useAuthoringActions"
import useAuthoringSaveActions from "./hooks/actions/useAuthoringSaveActions"
import useResultsTableViewStore from "./hooks/stores/useResultsTableViewStore"
import useChatbotStore from "../../../../../context/stores/useChatbotStore"

// styles
import "./Authoring.scss"
import "styles/authoring-print.scss"
import "./index.css"

const Authoring = ({ study, authoring, formDataRef }) => {
  const { sidebarOpen } = useAppState()
  const location = useLocation()
  const { setChatbotContext, resetChatbotContext } = useChatbotStore(
    useShallow((state) => ({
      setChatbotContext: state.setChatbotContext,
      resetChatbotContext: state.resetChatbotContext,
    }))
  )

  const { onDeleteFork } = useAuthoringActions(
    useShallow((state) => ({
      onDeleteFork: state.onDeleteFork,
    }))
  )

  const {
    authoringId,
    setAuthoringId,
    isUserLocked,
    setIsUserLocked,
    setShowSaveLock,
    enableAutoSave,
    setEnableAutoSave,
    showOverrideModal,
    setShowOverrideModal,
    overrideCallback,
  } = useAuthoringStore(
    useShallow((state) => ({
      authoringId: state.authoringId,
      setAuthoringId: state.setAuthoringId,
      isUserLocked: state.isUserLocked,
      setIsUserLocked: state.setIsUserLocked,
      setShowSaveLock: state.setShowSaveLock,
      setCustomTableWidthClassName: state.setCustomTableWidthClassName,
      enableAutoSave: state.enableAutoSave,
      setEnableAutoSave: state.setEnableAutoSave,
      showOverrideModal: state.showOverrideModal,
      setShowOverrideModal: state.setShowOverrideModal,
      overrideCallback: state.overrideCallback,
    }))
  )

  const {
    formData,
    setFormData,
    forkedStudyData,
    setForkedStudyData,
    overrideData,
    setOverrideData,
    setAttachedDocuments,
    translationsData,
    setTranslationsData,
  } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
      setFormData: state.setFormData,
      forkedStudyData: state.forkedStudyData,
      setForkedStudyData: state.setForkedStudyData,
      overrideData: state.overrideData,
      setOverrideData: state.setOverrideData,
      setAttachedDocuments: state.setAttachedDocuments,
      translationsData: state.translationsData,
      setTranslationsData: state.setTranslationsData,
    }))
  )

  const {
    isReviewerView,
    showPagePreview,
    formView,
    formSection,
    formStatus,
    setFormStatus,
    currDataSchema,
    currUISchema,
    showPanel,
    setShowPanel,
    showNav,
    setCurrDataSchema,
    setCurrUISchema,
    setFormView,
    setFormSection,
    readOnly,
    refreshFormData,
    fullScreen,
    setFullScreen,
    setCustomTableWidthClassName,
    formWidthClassName,
    setFormWidthClassName,
    showDeleteForkModal,
    setShowDeleteForkModal,
    loadingForm,
  } = useAuthoringViewStore(
    useShallow((state) => ({
      isReviewerView: state.isReviewerView,
      showPagePreview: state.showPagePreview,
      formView: state.formView,
      formSection: state.formSection,
      formStatus: state.formStatus,
      setFormStatus: state.setFormStatus,
      currDataSchema: state.currDataSchema,
      currUISchema: state.currUISchema,
      showPanel: state.showPanel,
      setShowPanel: state.setShowPanel,
      setCurrDataSchema: state.setCurrDataSchema,
      setCurrUISchema: state.setCurrUISchema,
      setFormView: state.setFormView,
      setFormSection: state.setFormSection,
      readOnly: state.readOnly,
      fullScreen: state.fullScreen,
      setFullScreen: state.setFullScreen,
      refreshFormData: state.refreshFormData,
      showNav: state.showNav,
      setCustomTableWidthClassName: state.setCustomTableWidthClassName,
      formWidthClassName: state.formWidthClassName,
      setFormWidthClassName: state.setFormWidthClassName,
      setShowDeleteForkModal: state.setShowDeleteForkModal,
      showDeleteForkModal: state.showDeleteForkModal,
      loadingForm: state.loadingForm,
    }))
  )
  const { hiddenFields, setHiddenFields, lockedFields, setLockedFields } = useAuthoringFieldStore(
    useShallow((state) => ({
      hiddenFields: state.hiddenFields,
      setHiddenFields: state.setHiddenFields,
      lockedFields: state.lockedFields,
      setLockedFields: state.setLockedFields,
    }))
  )

  const { setRegistryValidations } = useAuthoringValidationsStore(
    useShallow((state) => ({
      setRegistryValidations: state.setRegistryValidations,
    }))
  )

  const { selectedVersion, setSelectedVersion, setVersionData, versionValues, setVersionValues } =
    useAuthoringVersionStore(
      useShallow((state) => ({
        selectedVersion: state.selectedVersion,
        setSelectedVersion: state.setSelectedVersion,
        setVersionData: state.setVersionData,
        versionValues: state.versionValues,
        setVersionValues: state.setVersionValues,
      }))
    )

  const { setParticipantView, setTableView } = useResultsTableViewStore()

  const { handleSubmit } = useAuthoringSaveActions()

  const queryClient = useQueryClient()
  useAuthoringComments()

  // hooks
  const { data: permissions } = useUserPermissions()
  const { data: sites } = useGetSiteManagementLocations(study?.site_mgmt_study_id)
  const { data: authoringVersion, refetch: refetchAuthoringVersion } = useGetAuthoringVersion(
    authoring?.id,
    selectedVersion?.version_num
  )
  const { data: lockedFieldsResp = [] } = useGetLockedFields()

  useEffect(() => {
    if (formView && formSection) {
      const schema = formSchemas[formSection.id].properties[formView.id]
      if (schema?.schema && schema?.uiSchema) {
        setCurrDataSchema(schema.schema)
        setCurrUISchema(typeof schema.uiSchema === "function" ? schema.uiSchema() : schema.uiSchema)
      } else {
        console.log("ERROR: schema not found:", {
          formView,
          formSection,
        })
      }
    }
    if (formView?.id !== "ctg") {
      setRegistryValidations(null)
    }
  }, [formView, formSection])

  useEffect(() => {
    if (formSection) {
      const formSectId = formSection.id
      if (!hiddenFields?.[formSectId]) {
        const hiddenNodes = {}
        for (const regKey in formSchemas[formSectId].properties) {
          if (regKey !== "global") {
            let uiSchema = formSchemas[formSectId].properties[regKey].uiSchema

            if (typeof uiSchema === "function") uiSchema = uiSchema()
            const nodes = getHiddenNodes(uiSchema, "root")
            if (nodes?.length > 0) {
              hiddenNodes[formSectId] = hiddenNodes[formSectId] || {}
              hiddenNodes[formSectId][regKey] = nodes
            }
          }
        }
        setHiddenFields(hiddenNodes)
      }
    }
  }, [formSection?.id, JSON.stringify(hiddenFields)])

  useEffect(() => {
    if (formSection?.id) {
      const reloadFormData = async () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.authoringStudies.detail(study?.authoring_study_id) })
      }

      reloadFormData()
    }
  }, [formSection?.id])

  React.useEffect(() => {
    if (authoring?.override_data) {
      setOverrideData(authoring.override_data)
    }
  }, [JSON.stringify(authoring?.override_data)])

  useEffect(() => {
    if (formView) {
      if (formView.id === "ctg" || formView.id === "global") {
        setTableView("ctg")
        setParticipantView("ctg_periods")
      } else if (formView.id === "eudra") {
        setTableView("eudract")
        setParticipantView("eudract_periods")
      }
    }
  }, [formView])

  useEffect(() => {
    const classNames = ["auth-form-width"]
    const customTableClassNames = []

    if (showPanel) {
      classNames.push("side-panel-open")
      customTableClassNames.push("side-panel-open")
    }

    if (sidebarOpen && !isReviewerView) {
      classNames.push("main-nav-open")
      customTableClassNames.push("main-nav-open")
    }

    if (showNav) {
      classNames.push("form-nav-open")
      customTableClassNames.push("form-nav-open")
    }

    if (fullScreen) {
      classNames.push("form-full-screen")
      customTableClassNames.push("form-full-screen")
    }

    setFormWidthClassName(classNames.join(" "))
    setCustomTableWidthClassName(customTableClassNames.join(" "))
  }, [showPanel, sidebarOpen, showNav, isReviewerView, fullScreen])

  /** VERSIONING */
  // version data to revert to
  // versioned data lookup table to quickly retrieve values

  // PAGE PREVIEW
  const [checkOverrideModal, setCheckOverrideModal] = useState(false)

  useEffect(() => {
    if (lockedFieldsResp && Array.isArray(lockedFieldsResp)) {
      let res = {}
      lockedFieldsResp.forEach((field) => {
        let jpath = "root_" + field.split(".").join("_") // format jpath by appending root_ and converting dot notation to underscore
        res[jpath] = field
      })

      setLockedFields(res)
    }
  }, [JSON.stringify(lockedFieldsResp)])

  useEffect(() => {
    if (study?.authoring_study_id) {
      setAuthoringId(study.authoring_study_id)
    }
    if (authoring?.locked) {
      setShowSaveLock(authoring.locked)
    }

    return () => {
      onResetStates()
      resetChatbotContext()
    }
  }, [])

  const onResetStates = () => {
    setEnableAutoSave(false)
    formDataRef.current = null
    setVersionValues({})
    setFormStatus("init")
    if (isReviewerView === false) {
      setFormView({ id: "global", text: "Global" })
      setFormSection({ id: "protocol", text: "Protocol" })
      setSelectedVersion(null)
      setVersionData(null)
    }
  }

  const _handleSubmit = React.useCallback(
    debounce((data) => handleSubmit(data), 500),
    [
      isUserLocked,
      enableAutoSave,
      permissions,
      isReviewerView,
      enableAutoSave,
      readOnly,
      forkedStudyData,
      overrideData,
      translationsData,
      formData,
    ]
  )

  useAutosave({
    data: formData,
    interval: 1000,
    onSave: (data) => {
      formDataRef.current = data
      _handleSubmit()
    },
  })

  useAutosave({
    data: translationsData,
    interval: 1000,
    onSave: () => {
      _handleSubmit()
    },
  })

  useAutosave({
    data: forkedStudyData,
    interval: 1000,
    onSave: () => {
      _handleSubmit()
    },
  })

  useAutosave({
    data: overrideData,
    interval: 1000,
    onSave: () => {
      _handleSubmit()
    },
  })

  useEffect(() => {
    if (isReviewerView) {
      setShowPanel(true)
    }
  }, [isReviewerView])

  useEffect(() => {
    if (selectedVersion) refetchAuthoringVersion()
  }, [selectedVersion])

  /**
   * VERSION COMPARE: when user selects a version
   * cache versions to lookup table
   */
  useEffect(() => {
    if (selectedVersion && authoringVersion) {
      const selectedVersNum = selectedVersion.version_num
      let versData = null
      // find version values and cache
      // getStudyVersion(authoring.id, selectedVersNum).then(res => {
      versData = authoringVersion?.data?.study_data

      if (versData) {
        setVersionValues({
          ...versionValues,
          [selectedVersNum]: {
            study_data: getVersionValues(versData),
            forked_data: versData?.forked_study_data,
          },
        })
        setVersionData(authoringVersion.data)
      }
    }
  }, [selectedVersion, authoringVersion])

  // INIT from StudyOverview authoring data
  useEffect(() => {
    onResetStates()
    initAuthoring()
    setTimeout(async () => {
      setFormStatus("ready")
    }, 1000)
  }, [authoring?.id])

  const enableForm = React.useCallback(
    debounce(async () => {
      await new Promise((r) => setTimeout(r, 2000))
      setEnableAutoSave(true)
    }, 1000),
    [setEnableAutoSave]
  )

  useEffect(() => {
    if (formStatus === "ready") {
      enableForm()
    }
  }, [formStatus])

  const initAuthoring = async () => {
    if (authoring?.attached_documents) {
      setAttachedDocuments(authoring?.attached_documents)
    }

    if (authoring) {
      let studyData = { ...authoring.study_data }
      let translationsData = authoring?.study_data?.translations || {}
      let forkedData = {
        ctg: {},
        eudract: {},
      }

      if (studyData.forked_study_data) {
        forkedData = { ...studyData.forked_study_data }
      }

      if (authoring.is_locked_for_user) {
        setIsUserLocked(true)
      }

      setForkedStudyData(forkedData)
      setTranslationsData(translationsData)
      setOverrideData(authoring?.override_data)
      formDataRef.current = studyData
      setFormData(studyData)
      await new Promise((r) => setTimeout(r, 1000))
      setChatbotContext({
        study: {
          ...authoring,
          study_data: {
            protocol: authoring?.study_data?.protocol,
            forked_study_data: authoring?.study_data?.forked_study_data,
          },
        },
        url: location.pathname,
      })
    }
  }

  /** EVENT HANDLERS */
  const confirmOverride = () => {
    setShowOverrideModal(false)

    if (checkOverrideModal) {
      localStorage.setItem("show-override-warning", false)
    }
    overrideCallback()
  }

  const renderAuthoringForm = useMemo(() => {
    const formContext = {
      formData,
      formDataRef: formDataRef.current,
      setFormData,
      handleSubmit: _handleSubmit,
    }

    const sitesContainerClassNames = ["doc-loc-panel-wrapper"]
    if (fullScreen === false) {
      if (showNav) sitesContainerClassNames.push("auth-form--open-nav")
      if (showPanel && isReviewerView === false) sitesContainerClassNames.push("auth-form--open-comments")
    }

    return (
      <>
        <RJForm
          formData={formData}
          schema={currDataSchema}
          uiSchema={currUISchema}
          ObjectFieldTemplate={ObjectFieldTemplate}
          ArrayFieldTemplate={ArrayFieldTemplate}
          onChange={(data) => {
            formDataRef.current = data
            setFormData(data)
          }}
          handleSubmit={_handleSubmit}
          formContext={formContext}
        />
        {formStatus === "ready" &&
          (formView?.id === "ctg" || formView?.id === "global") &&
          formSection?.id === "protocol" && (
            <div className="ag-doc-sites-container">
              <div
                className={sitesContainerClassNames.join(" ")}
                id="locations-container"
              >
                <div className="authoring-form-section-panel-h1">Locations</div>
                <SitesGrid
                  study={study}
                  sites={sites}
                />
              </div>
            </div>
          )}
      </>
    )
  }, [
    authoring,
    readOnly,
    formSection,
    formView,
    currDataSchema,
    currUISchema,
    showPagePreview,
    formWidthClassName,
    overrideData,
    forkedStudyData,
    selectedVersion,
    refreshFormData,
    formStatus,
    showPanel,
    sidebarOpen,
    showNav,
    lockedFields,
    loadingForm,
  ])

  if (formStatus !== "ready" || !formView || !currDataSchema || !currUISchema || !authoring || !authoringId) {
    return (
      <Flex
        justify="center"
        align="center"
        style={{
          width: "100%",
        }}
      >
        <Loading position="fixed" />
      </Flex>
    )
  }

  const onEscapeFullView = (e) => {
    if (e.key === "Escape") {
      onExitFullScreen()
    }
  }

  const onExitFullScreen = () => {
    exitFullscreen()
    setFullScreen(false)
  }

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen()
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen()
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen()
    }
  }

  let classNames = [`prime-authoring-container`]
  if (isReviewerView) classNames.push("reviewer-view")
  const authoringFormClassNames = ["authoring-form-container"]

  if (fullScreen) {
    authoringFormClassNames.push("auth-form-full-screen")
  }

  if (showPanel && !fullScreen) {
    authoringFormClassNames.push("authoring-form-container--with-panel")
  }

  return (
    <div
      id="prime-authoring-container"
      className={[...classNames, formWidthClassName].join(" ")}
    >
      {/* {!isReviewerView && renderFormActions} */}
      <div
        className="authoring-content-container"
        // id="authoring-print-page-container"
      >
        <div
          className={authoringFormClassNames.join(" ")}
          id="authoring-form-container"
          onKeyDown={onEscapeFullView}
          tabIndex="0"
        >
          {showPagePreview === true ? (
            <div id="page-preview-container">
              <PagePreview
                forkedStudyData={forkedStudyData}
                overrideData={overrideData}
                authoring={{
                  id: authoring.id,
                  study_data: formData,
                }}
              />
            </div>
          ) : (
            renderAuthoringForm
          )}
        </div>
        <FloatButton.BackTop
          visibilityHeight={600}
          target={() => document.querySelector(".right-section")}
        />
      </div>
      <Modal
        title={"Override data?"}
        message={
          "This field value is set by a source system. Manually entered data in this field will disconnect the source system feed into this field until the override is removed."
        }
        type="delete"
        open={showOverrideModal}
        onCancel={() => setShowOverrideModal(false)}
        footer={[]}
        width={400}
      >
        <div className="override-modal-content">
          <div className="override-modal-actions">
            <PrimeButton
              // size="small"
              style={{
                marginRight: 10,
              }}
              onClick={() => setShowOverrideModal(false)}
            >
              Cancel
            </PrimeButton>
            <PrimeButton
              // size="small"
              onClick={confirmOverride}
              type="primary"
            >
              Override
            </PrimeButton>
          </div>
          <PrimeField
            schema={{
              type: "checkbox",
              label: "Don't show this message again",
            }}
            value={checkOverrideModal}
            onChange={setCheckOverrideModal}
          />
        </div>
      </Modal>
      <ForkDeleteModal
        onHide={() => setShowDeleteForkModal(false)}
        showModal={showDeleteForkModal}
        onSubmit={onDeleteFork}
      />
      {fullScreen && (
        <>
          <Tooltip
            className="auth-minimize-button"
            title="Exit full screen"
          >
            <div onClick={onExitFullScreen}>
              <i className="far fa-compress" />
            </div>
          </Tooltip>
          <FloatButton.BackTop
            target={() => document.querySelector(".auth-form-full-screen")}
            visibilityHeight={600}
            style={{ marginBottom: "30px" }}
          />
        </>
      )}
    </div>
  )
}

export default Authoring
